import { Lock, LockOpen, Pencil, Trash2, UserPlus, Users, X } from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";

import { AsyncBoundary, EmptyState } from "@/components/data-state";
import { DateTimePicker } from "@/components/date-time-picker";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { useAsync } from "@/hooks/use-async";
import { useAdminCourse, useAdminKey } from "@/hooks/use-auth";
import type { AdminGroup, Course, StudentKeyEntry } from "@/lib/api";
import { ApiError, adminApi } from "@/lib/api";
import { formatRelative } from "@/lib/format";
import { cn } from "@/lib/utils";

/**
 * Teams, as the teaching team sees them.
 *
 * Students form teams themselves (the "My team" page); this page is for the policy that
 * bounds them and for fixing what they get wrong. Admins are not bound by the policy:
 * they can add to a full team, move someone, or act after changes are closed.
 *
 * The policy is on the course -- size cap, open/closed, an optional lock time -- and
 * lives at the top because it is the one thing here that affects every student at once.
 * The ungrouped list is the other thing worth seeing first: those are the people to
 * message, and the ones to form into teams by hand when the deadline has passed.
 */
export function AdminGroupsRoute() {
  const adminKey = useAdminKey();
  const courseId = useAdminCourse();
  const [renaming, setRenaming] = useState<AdminGroup | null>(null);
  const [renameValue, setRenameValue] = useState("");
  const [deleting, setDeleting] = useState<AdminGroup | null>(null);
  const [picked, setPicked] = useState<Set<number>>(new Set());
  const [busy, setBusy] = useState(false);

  const groups = useAsync(adminKey && courseId ? () => adminApi.groups(courseId, adminKey) : null, [adminKey, courseId]);
  const keys = useAsync(adminKey && courseId ? () => adminApi.keys(adminKey, courseId) : null, [adminKey, courseId]);
  const courses = useAsync(adminKey ? () => adminApi.courses(adminKey) : null, [adminKey]);
  const course = courses.data?.find((c) => c.id === courseId) ?? null;

  // Students (not staff) with no team; the pool every "add" draws from. One row per
  // student even if they hold several keys, and students whose keys are all disabled
  // are kept -- greyed -- rather than hidden: a list that quietly omits someone is how
  // an admin ends up asking why the count is short.
  const grouped = useMemo(() => new Set((groups.data ?? []).flatMap((g) => g.members.map((m) => m.student_id))), [groups.data]);
  const ungrouped = useMemo(() => {
    const byStudent = new Map<number, StudentKeyEntry & { any_active: boolean }>();
    for (const k of keys.data ?? []) {
      if (k.role === "staff" || grouped.has(k.student_id)) continue;
      const seen = byStudent.get(k.student_id);
      byStudent.set(k.student_id, { ...(seen ?? k), any_active: (seen?.any_active ?? false) || k.active });
    }
    return [...byStudent.values()].sort((a, b) => Number(b.any_active) - Number(a.any_active) || (a.name || a.email).localeCompare(b.name || b.email));
  }, [keys.data, grouped]);

  function refetch() {
    groups.refetch();
    setPicked(new Set());
  }

  async function act(fn: () => Promise<unknown>, done: string) {
    if (!adminKey) return;
    setBusy(true);
    try {
      await fn();
      toast.success(done);
      refetch();
    } catch (caught) {
      toast.error(caught instanceof ApiError ? caught.message : "Failed", { duration: 10000 });
    } finally {
      setBusy(false);
    }
  }

  const nextName = () => {
    const taken = new Set((groups.data ?? []).map((g) => g.name));
    let n = taken.size + 1;
    while (taken.has(`Team ${n}`)) n += 1;
    return `Team ${n}`;
  };

  if (!courseId) return <p className="text-muted-foreground text-sm">Choose a course above to continue.</p>;

  return (
    <div className="space-y-6">
      {course ? (
        <PolicyBar
          course={course}
          onChange={(patch) => act(() => adminApi.patchCourse(course.id, patch, adminKey!).then(() => courses.refetch()), "Policy saved")}
        />
      ) : null}

      <AsyncBoundary state={groups} empty={null}>
        {(list) => {
          const max = course?.team_max_size ?? 3;
          const underSize = list.filter((g) => g.members.length < max).length;
          return (
            <>
              <p className="text-muted-foreground text-sm">
                {list.length} team{list.length === 1 ? "" : "s"} · {grouped.size} students on one · {ungrouped.length} not
                {underSize ? ` · ${underSize} team${underSize === 1 ? "" : "s"} under ${max}` : ""}
              </p>

              {ungrouped.length ? (
                <Card className="border-amber-600/30 bg-amber-500/5">
                  <CardHeader>
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div>
                        <CardTitle className="text-sm">
                          {ungrouped.length} student{ungrouped.length === 1 ? "" : "s"} not on a team
                        </CardTitle>
                        <CardDescription className="text-xs">
                          They rank on their own. Tick some to form a team of them, or add one to an existing team below.
                        </CardDescription>
                      </div>
                      <Button
                        size="sm"
                        disabled={!picked.size || busy}
                        onClick={() => act(() => adminApi.createGroup(courseId, nextName(), adminKey!, [...picked]), `Formed a team of ${picked.size}`)}
                      >
                        <Users className="size-4" aria-hidden />
                        Form a team of {picked.size || "…"}
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <ul className="grid gap-1 sm:grid-cols-2 lg:grid-cols-3">
                      {ungrouped.map((s) => (
                        <li key={s.student_id} className={cn("flex items-center gap-2 text-sm", !s.any_active && "opacity-60")}>
                          <Checkbox
                            aria-label={`Pick ${s.email}`}
                            checked={picked.has(s.student_id)}
                            onCheckedChange={() =>
                              setPicked((prev) => {
                                const next = new Set(prev);
                                if (next.has(s.student_id)) next.delete(s.student_id);
                                else next.add(s.student_id);
                                return next;
                              })
                            }
                          />
                          <span className="truncate">{s.name || s.email}</span>
                          <span className="text-muted-foreground truncate font-mono text-xs">{s.email}</span>
                          {!s.any_active ? (
                            <Badge variant="outline" className="text-[10px]">
                              key disabled
                            </Badge>
                          ) : null}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              ) : null}

              {list.length === 0 ? (
                <EmptyState title="No teams yet" description="Students form their own from the My team page; or tick some above." />
              ) : (
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {list.map((group) => (
                    <TeamCard
                      key={group.id}
                      group={group}
                      max={max}
                      pool={ungrouped}
                      busy={busy}
                      onRename={() => {
                        setRenaming(group);
                        setRenameValue(group.name);
                      }}
                      onDisband={() => setDeleting(group)}
                      onAdd={(sid) => act(() => adminApi.addGroupMember(group.id, sid, adminKey!), "Added")}
                      onRemove={(sid) => act(() => adminApi.removeGroupMember(group.id, sid, adminKey!), "Removed")}
                    />
                  ))}
                </div>
              )}
            </>
          );
        }}
      </AsyncBoundary>

      <Dialog open={renaming !== null} onOpenChange={(o) => !o && setRenaming(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Rename team</DialogTitle>
          </DialogHeader>
          <div className="grid gap-2">
            <Label htmlFor="rename">Name</Label>
            <Input
              id="rename"
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && renaming && renameValue.trim()) {
                  void act(() => adminApi.renameGroup(renaming.id, renameValue.trim(), adminKey!), "Renamed").then(() => setRenaming(null));
                }
              }}
            />
          </div>
          <DialogFooter>
            <Button
              disabled={!renameValue.trim() || busy}
              onClick={() => renaming && act(() => adminApi.renameGroup(renaming.id, renameValue.trim(), adminKey!), "Renamed").then(() => setRenaming(null))}
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleting !== null} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Disband {deleting?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              Submissions are kept. Its {deleting?.members.length ?? 0} member{deleting?.members.length === 1 ? "" : "s"} will rank individually.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => deleting && act(() => adminApi.deleteGroup(deleting.id, adminKey!), "Disbanded").then(() => setDeleting(null))}>
              Disband
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

/** The course's team policy, edited in place. Every change saves. */
function PolicyBar({ course, onChange }: { course: Course; onChange: (patch: Partial<Course>) => void }) {
  // Read once per mount; the lock time is set to the future by hand, so a stale "now" is
  // only wrong for someone who leaves the page open across the deadline.
  const [now] = useState(() => Date.now());
  const lockedByTime = course.teams_lock_at !== null && course.teams_lock_at <= now;
  const open = course.teams_open && !lockedByTime;
  return (
    <Card>
      <CardContent className="flex flex-wrap items-center gap-x-6 gap-y-3">
        <div className="flex items-center gap-2">
          {open ? <LockOpen className="text-muted-foreground size-4" aria-hidden /> : <Lock className="size-4" aria-hidden />}
          <Label htmlFor="teams-open" className="font-medium">
            Students may change teams
          </Label>
          <Switch id="teams-open" checked={course.teams_open} onCheckedChange={(next) => onChange({ teams_open: next })} />
          {lockedByTime && course.teams_open ? <Badge variant="secondary">closed by the time below</Badge> : null}
        </div>
        <div className="flex items-center gap-2">
          <Label htmlFor="teams-lock" className="text-muted-foreground text-xs">
            Close automatically at
          </Label>
          <DateTimePicker
            id="teams-lock"
            placeholder="Never"
            value={course.teams_lock_at}
            onChange={(teams_lock_at) => onChange({ teams_lock_at })}
          />
        </div>
        <div className="flex items-center gap-2">
          <Label htmlFor="team-max" className="text-muted-foreground text-xs">
            Max team size
          </Label>
          <Input
            id="team-max"
            type="number"
            min={1}
            max={20}
            className="h-8 w-16 text-xs"
            defaultValue={course.team_max_size}
            onBlur={(e) => {
              const n = Number(e.target.value);
              if (n >= 1 && n <= 20 && n !== course.team_max_size) onChange({ team_max_size: n });
            }}
          />
        </div>
        <p className="text-muted-foreground w-full text-xs">
          Standings resolve team membership when read, so close changes before a competition starts. You are never bound by these.
        </p>
      </CardContent>
    </Card>
  );
}

function TeamCard({
  group,
  max,
  pool,
  busy,
  onRename,
  onDisband,
  onAdd,
  onRemove,
}: {
  group: AdminGroup;
  max: number;
  pool: StudentKeyEntry[];
  busy: boolean;
  onRename: () => void;
  onDisband: () => void;
  onAdd: (studentId: number) => void;
  onRemove: (studentId: number) => void;
}) {
  const items = Object.fromEntries(pool.map((s) => [String(s.student_id), s.name || s.email]));
  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <CardTitle className="flex items-center gap-2 truncate text-base">
              {group.name}
              <Badge variant={group.members.length > max ? "destructive" : "secondary"} className="font-mono text-[10px]">
                {group.members.length}/{max}
              </Badge>
            </CardTitle>
            <p className="text-muted-foreground text-xs">
              {group.created_by ? `formed by ${group.created_by}` : "made by staff"}
              {group.created_at ? ` · ${formatRelative(group.created_at)}` : ""}
            </p>
          </div>
          <div className="flex shrink-0 gap-1">
            <Button variant="ghost" size="icon-sm" aria-label="Rename" onClick={onRename}>
              <Pencil className="size-3.5" aria-hidden />
            </Button>
            <Button variant="ghost" size="icon-sm" aria-label="Disband" onClick={onDisband}>
              <Trash2 className="size-3.5" aria-hidden />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {group.members.length ? (
          <ul className="divide-y rounded-md border text-sm">
            {group.members.map((m) => (
              <li key={m.student_id} className="flex items-center gap-2 px-2.5 py-1.5">
                <div className="min-w-0 flex-1">
                  <p className="truncate">{m.name || m.email}</p>
                  <p className="text-muted-foreground truncate text-xs">
                    <span className="font-mono">{m.email}</span>
                    {m.added_by ? ` · added by ${m.added_by}` : ""}
                  </p>
                </div>
                <Button variant="ghost" size="icon-sm" className="text-muted-foreground hover:text-destructive" aria-label={`Remove ${m.email}`} disabled={busy} onClick={() => onRemove(m.student_id)}>
                  <X className="size-3.5" aria-hidden />
                </Button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-muted-foreground text-sm">No members</p>
        )}
        {pool.length ? (
          <Select items={items} value={null} onValueChange={(v) => v && onAdd(Number(v))}>
            <SelectTrigger size="sm" className="w-full" aria-label={`Add a member to ${group.name}`}>
              <UserPlus className="size-3.5" aria-hidden />
              <SelectValue placeholder="Add an ungrouped student…" />
            </SelectTrigger>
            <SelectContent>
              {pool.map((s) => (
                <SelectItem key={s.student_id} value={String(s.student_id)}>
                  {s.name || s.email}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : null}
      </CardContent>
    </Card>
  );
}
